import React, { useEffect, useState } from 'react';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';

import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { GlobalSearch } from '@/components/shell/GlobalSearch';
import SyncIndicator from '@/components/SyncIndicator';

/**
 * La barre supérieure du bureau.
 *
 * Elle porte ce qui ne dépend pas de l'écran affiché: chercher dans tout le
 * produit, savoir ce qui attend une décision, accéder à son compte. Avant, ces
 * trois choses vivaient dans les pages — la recherche s'était dupliquée sur
 * cinq écrans, et le nombre d'alertes en attente n'était visible que sur la
 * page des alertes, c'est-à-dire exactement là où l'on est déjà au courant.
 *
 * Le compteur d'alertes est rafraîchi toutes les deux minutes et **jamais plus
 * souvent**: c'est une requête posée sur les quarante-six écrans du produit, et
 * une file d'alertes ne se remplit pas à la seconde. Il échoue en silence — un
 * badge qui manque vaut mieux qu'une erreur qui masque l'écran.
 */

const RAFRAICHISSEMENT_MS = 120_000;

const useNombreAlertes = () => {
  const { hasModule } = useAuth();
  const [nombre, setNombre] = useState(0);
  const autorise = typeof hasModule === 'function' ? hasModule('alerts') : false;
  // Une décision prise sur une alerte, ou une écriture qui en crée/éteint
  // (paiement, trajet, document…), relit le compteur sans attendre les deux
  // minutes du minuteur.
  const [version, setVersion] = useState(0);
  useRafraichirSur(['alertes'], () => setVersion((valeur) => valeur + 1), { actif: autorise });

  useEffect(() => {
    if (!autorise) {
      setNombre(0);
      return undefined;
    }
    let annule = false;

    const lire = async () => {
      try {
        const reponse = await api.get('/alerts/events/summary');
        const donnees = reponse?.data?.data || reponse?.data || {};
        // `to_handle`, tel que le nomme `summarize_alert_events`: ce qui
        // attend une décision. Ni les alertes déjà prises en charge ni les
        // escaladées — celles-là sont suivies, elles n'appellent pas à
        // interrompre ce qu'on fait. Repli à 0 plutôt que `NaN` si le contrat
        // du serveur évolue.
        const ouvertes = Number(donnees.to_handle ?? 0);
        if (!annule) setNombre(Number.isFinite(ouvertes) ? ouvertes : 0);
      } catch (erreur) {
        if (!annule) setNombre(0);
      }
    };

    lire();
    const minuteur = setInterval(lire, RAFRAICHISSEMENT_MS);
    return () => {
      annule = true;
      clearInterval(minuteur);
    };
  }, [autorise, version]);

  return nombre;
};

export const TopBar = ({ className }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const alertes = useNombreAlertes();

  const initiales = (user?.full_name || user?.email || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((mot) => mot[0])
    .join('')
    .toUpperCase();

  return (
    <header
      className={cn(
        'sticky top-0 z-30 hidden h-16 items-center gap-3 border-b border-border bg-background/85 px-4 backdrop-blur md:px-6 lg:flex lg:px-8',
        className,
      )}
    >
      <GlobalSearch className="w-full max-w-xl" />

      <div className="ml-auto flex items-center gap-2">
        <SyncIndicator inline />

        <button
          type="button"
          onClick={() => navigate('/alerts')}
          aria-label={
            alertes > 0
              ? `${alertes} alerte${alertes > 1 ? 's' : ''} à traiter`
              : 'Alertes'
          }
          className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Bell className="h-[18px] w-[18px]" />
          {/* Le compte, pas une pastille muette: « 3 » dit s'il faut y aller
              maintenant, un point ne dit rien. Au-delà de neuf, le nombre
              exact n'ajoute plus rien à la décision. */}
          {alertes > 0 ? (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-flame px-1 text-[10px] font-bold tabular-nums text-flame-foreground">
              {alertes > 9 ? '9+' : alertes}
            </span>
          ) : null}
        </button>

        <button
          type="button"
          onClick={() => navigate('/profile')}
          aria-label="Mon compte"
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 font-display text-sm font-bold text-primary transition-colors hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {initiales}
        </button>
      </div>
    </header>
  );
};

export default TopBar;
